import React from 'react';
import { FaFacebookF, FaXTwitter, FaInstagram, FaLinkedinIn, FaTiktok } from 'react-icons/fa6';
import { Trans, useTranslation } from 'react-i18next';

const NMLS_COMPANY_HREF = 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/2508833';
const NMLS_227977_HREF = 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/227977';
const NMLS_1810501_HREF = 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/1810501';
const NMLS_1475872_HREF = 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/1475872';
const PARTNERS_HREF = 'https://symplelending.com/partners';

const NmlsLink = ({ href, children }: { href: string; children?: React.ReactNode }) => (
  <a
    href={href}
    className="text-[#212d52] hover:underline"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

export default function Footer() {
  const { t } = useTranslation('common');

  const legalLinks = [
    { key: 'privacyPolicy', href: 'https://symplelending.com/privacy-policy' },
    { key: 'termsOfUse', href: 'https://symplelending.com/terms-of-use' },
    { key: 'caPrivacyPolicy', href: 'https://symplelending.com/california-privacy-policy' },
    { key: 'optOut', href: 'https://symplelending.com/opt-out' },
    { key: 'eCommConsent', href: 'https://symplelending.com/esign-consent' },
    { key: 'privacyNotice', href: 'https://symplelending.com/privacy-notice' },
  ] as const;

  const socialLinks = [
    { Icon: FaFacebookF, href: 'https://www.facebook.com/symplelending/', labelKey: 'facebook' },
    { Icon: FaXTwitter, href: 'https://x.com/symplelend/?mx=2', labelKey: 'x' },
    { Icon: FaInstagram, href: 'https://www.instagram.com/symplelending/#', labelKey: 'instagram' },
    { Icon: FaLinkedinIn, href: 'https://www.linkedin.com/company/symple-lending/', labelKey: 'linkedin' },
    { Icon: FaTiktok, href: 'https://www.tiktok.com/@symple_lending', labelKey: 'tiktok' },
  ] as const;

  return (
    <footer className="bg-white mt-12 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-10 sm:px-6 lg:px-8">

        {/* Logo + Social Icons */}
        <div className="flex flex-col-reverse items-center gap-4 mb-6 sm:flex-row sm:justify-between sm:gap-0">
          <img
            src="/symple-lending-logo.png"
            alt={t('footer.logoAlt')}
            className="h-10"
          />
          <div className="flex items-center gap-4">
            {socialLinks.map(({ Icon, href, labelKey }) => (
              <a
                key={href}
                href={href}
                className="text-gray-600 hover:text-[#212d52] transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t(`footer.socialLabels.${labelKey}`)}
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>

        {/* Legal Links */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mb-3 sm:justify-start">
          {legalLinks.map((link, index) => (
            <React.Fragment key={link.href}>
              <a
                href={link.href}
                className="text-sm text-gray-600 hover:text-[#212d52] transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(`footer.legalLinks.${link.key}`)}
              </a>
              {index < legalLinks.length - 1 && (
                <span className="text-gray-300 select-none">|</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Copyright + NMLS */}
        <p className="text-sm text-gray-500 mb-8 text-center sm:text-left">
          <Trans
            i18nKey="footer.copyright"
            ns="common"
            values={{ year: new Date().getFullYear() }}
            components={{ nmlsLink: <NmlsLink href={NMLS_COMPANY_HREF} /> }}
          />
        </p>

        {/* Disclosure */}
        <p className="text-sm text-gray-500 max-w-5xl mb-8">
          <Trans
            i18nKey="footer.disclosure"
            ns="common"
            components={{ nmlsLink: <NmlsLink href={NMLS_COMPANY_HREF} /> }}
          />
        </p>

        {/* Equal Housing Lender */}
        <div className="mb-6">
          <img
            src="/equal-housing-lender.png"
            alt={t('footer.equalHousingAlt')}
            className="h-12"
          />
        </div>

        {/* Partners Licensing Information */}
        <div className="text-sm text-gray-600">
          <p className="mb-2">
            <Trans
              i18nKey="footer.partnersIntro"
              ns="common"
              components={{
                partnersLink: (
                  <a
                    href={PARTNERS_HREF}
                    className="font-semibold text-[#212d52] hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-500">
            <li>
              <Trans
                i18nKey="footer.partnerAchieve"
                ns="common"
                components={{
                  nmls227977: <NmlsLink href={NMLS_227977_HREF} />,
                  nmls1810501: <NmlsLink href={NMLS_1810501_HREF} />,
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey="footer.partnerMoneyLion"
                ns="common"
                components={{ nmls1475872: <NmlsLink href={NMLS_1475872_HREF} /> }}
              />
            </li>
          </ul>
        </div>

      </div>
    </footer>
  );
}
